import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCustomFieldsConfig } from '@work4all/data/lib/custom-fields';
import { useDataMutation } from '@work4all/data/lib/hooks/data-provider/useDataMutation';

import { LookUp } from '@work4all/models/lib/Classes/LookUp.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';

import { mapEntityVariantToLookupType } from '../../../../../utils/lookup-type-mapping';
import { MaskTab, MaskTabPanel, MaskTabs } from '../../../mask-tabs';
import { IndividualTabPanel } from '../../components/custom-fields/IndividualTabPanel';
import { prepareInputWithCustomFields } from '../../components/custom-fields/prepare-input-with-custom-fields';
import { MaskContent } from '../../components/MaskContent/MaskContent';
import { useMaskConfig } from '../../hooks/mask-context';
import { OverlayController } from '../../overlay-controller/OverlayController';
import { useMaskOverlay } from '../../overlay-controller/use-mask-overlay';
import { MaskControllerProps } from '../../types';
import { pickUpdateFields } from '../../utils/pick-update-fields';

import { GeneralTabPanel } from './tabs/GeneralTabPanel';
import { useLookUpRequest } from './use-lookup-request';
import { useNewLookUpData } from './use-new-lookup-data';

type LookUpMaskFormValue = LookUp;

export const LookUpOverlayController = (props: MaskControllerProps) => {
  const mask = useMaskConfig(props);

  const customFields = useCustomFieldsConfig({ entity: mask.entity });

  // Map entity variant to LookupType
  const lookupType = mapEntityVariantToLookupType(mask.entityVariant);
  const { t } = useTranslation();

  const maskWithTitle = useMemo(() => {
    return {
      ...mask,
      params: {
        ...mask.params,
        customizedTitle: t(`COMMON.${mask.entityVariant.toUpperCase()}`),
      },
    };
  }, [mask, t]);

  const request = useLookUpRequest({
    lookupType,
    id:
      typeof mask.id === 'number'
        ? mask.id
        : mask.id
        ? parseInt(mask.id)
        : undefined,
  });

  const newEntityData = useNewLookUpData({
    enabled: mask.isCreateMode,
    lookupType,
  });

  const schema = useMemo(() => {
    return {
      type: 'object',
      required: [],
      properties: {
        name: {
          type: 'string',
          maxLength: 100,
        },
        standard: {
          type: 'integer',
        },
      },
    };
  }, []);

  const overlay = useMaskOverlay<LookUpMaskFormValue>({
    ...props,
    request,
    newEntityData,
    mask: maskWithTitle,
    customFields,
    getSubTitle: (x) => x.name,
    schema,
  });

  const { form } = overlay;

  const [mutate] = useDataMutation<LookUpMaskFormValue, EMode.upsert>({
    entity: mask.entity,
    mutationType: EMode.upsert,
    responseData: request.data,
    onCompleted: (data) => {
      if (mask.isCreateMode) {
        props.onAfterSave(data);
      } else {
        props.onAfterSave(data);
      }
    },
  });

  const handleSubmit = useCallback(
    async (values: LookUpMaskFormValue) => {
      let updateRaw = mask.isCreateMode
        ? values
        : pickUpdateFields(values, form.formState.dirtyFields);

      // Ensure lookupType is set from mapped type if in create mode
      if (mask.isCreateMode && lookupType) {
        updateRaw = { ...updateRaw, lookupType };
      }

      const updateMapped = prepareInputWithCustomFields(
        updateRaw as any,
        customFields
      );
      updateMapped.hexColorPair = undefined;
      await mutate(updateMapped);
    },
    [mask.isCreateMode, lookupType, form.formState.dirtyFields, mutate]
  );

  const shouldRenderIndividualTab = customFields && customFields.length > 0;

  return (
    <OverlayController<LookUpMaskFormValue>
      {...overlay}
      onSubmit={handleSubmit}
      tabs={
        <LookUpMaskTabs
          isCreateMode={mask.isCreateMode}
          renderIndividualTab={shouldRenderIndividualTab}
        />
      }
    >
      <LookUpMaskContent renderIndividualTab={shouldRenderIndividualTab} />
    </OverlayController>
  );
};

const LookUpMaskTabs = memo(function LookUpMaskTabs({
  renderIndividualTab,
}: {
  isCreateMode: boolean;
  renderIndividualTab: boolean;
}) {
  const { t } = useTranslation();

  return (
    <MaskTabs>
      <MaskTab value="general" label={t('MASK.GENERAL')} />

      {renderIndividualTab && (
        <MaskTab value="individual" label={t('MASK.INDIVIDUAL')} />
      )}
    </MaskTabs>
  );
});

const LookUpMaskContent = memo(function LookUpMaskContent({
  renderIndividualTab,
}: {
  renderIndividualTab: boolean;
}) {
  return (
    <MaskContent>
      <MaskTabPanel value="general">
        <GeneralTabPanel />
      </MaskTabPanel>

      {renderIndividualTab && (
        <MaskTabPanel value="individual">
          <IndividualTabPanel />
        </MaskTabPanel>
      )}
    </MaskContent>
  );
});
